import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('adminToken');

// Set auth header
const authHeader = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`
  }
});

// Public APIs
export const api = {
  // Projects
  getProjects: async () => {
    const response = await axios.get(`${API}/projects`);
    return response.data;
  },

  // Articles
  getArticles: async () => {
    const response = await axios.get(`${API}/articles`);
    return response.data;
  },

  getArticle: async (id) => {
    const response = await axios.get(`${API}/articles/${id}`);
    return response.data;
  },

  // Contact
  submitContact: async (data) => {
    const response = await axios.post(`${API}/contact`, data);
    return response.data;
  },

  // Analytics
  trackPageView: async (page, sessionId) => {
    try {
      await axios.post(`${API}/analytics/pageview`, { page, sessionId });
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }
  },

  // Experience (public)
  getExperience: async () => {
    const response = await axios.get(`${API}/experience`);
    return response.data;
  },

  // Settings (public)
  getSettings: async () => {
    const response = await axios.get(`${API}/settings`);
    return response.data;
  }
};

// Admin APIs
export const adminApi = {
  // Auth
  login: async (credentials) => {
    const response = await axios.post(`${API}/admin/login`, credentials);
    if (response.data.token) {
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUser', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  },

  isAuthenticated: () => {
    return !!getAuthToken();
  },

  getUser: () => {
    const user = localStorage.getItem('adminUser');
    return user ? JSON.parse(user) : null;
  },

  // Analytics
  getAnalyticsSummary: async () => {
    const response = await axios.get(`${API}/admin/analytics/summary`, authHeader());
    return response.data;
  },

  // Contacts
  getContacts: async () => {
    const response = await axios.get(`${API}/admin/contacts`, authHeader());
    return response.data;
  },

  updateContactStatus: async (id, status) => {
    const response = await axios.patch(`${API}/admin/contacts/${id}`, { status }, authHeader());
    return response.data;
  },

  // Projects
  getAllProjects: async () => {
    const response = await axios.get(`${API}/admin/projects`, authHeader());
    return response.data;
  },

  createProject: async (data) => {
    const response = await axios.post(`${API}/admin/projects`, data, authHeader());
    return response.data;
  },

  updateProject: async (id, data) => {
    const response = await axios.put(`${API}/admin/projects/${id}`, data, authHeader());
    return response.data;
  },

  deleteProject: async (id) => {
    const response = await axios.delete(`${API}/admin/projects/${id}`, authHeader());
    return response.data;
  },

  // Articles
  getAllArticles: async () => {
    const response = await axios.get(`${API}/admin/articles`, authHeader());
    return response.data;
  },

  createArticle: async (data) => {
    const response = await axios.post(`${API}/admin/articles`, data, authHeader());
    return response.data;
  },

  updateArticle: async (id, data) => {
    const response = await axios.put(`${API}/admin/articles/${id}`, data, authHeader());
    return response.data;
  },

  deleteArticle: async (id) => {
    const response = await axios.delete(`${API}/admin/articles/${id}`, authHeader());
    return response.data;
  },

  // Experience
  getAllExperiences: async () => {
    const response = await axios.get(`${API}/admin/experience`, authHeader());
    return response.data;
  },

  createExperience: async (data) => {
    const response = await axios.post(`${API}/admin/experience`, data, authHeader());
    return response.data;
  },

  updateExperience: async (id, data) => {
    const response = await axios.put(`${API}/admin/experience/${id}`, data, authHeader());
    return response.data;
  },

  deleteExperience: async (id) => {
    const response = await axios.delete(`${API}/admin/experience/${id}`, authHeader());
    return response.data;
  },

  // Visitors (All)
  getAllVisitors: async () => {
    const response = await axios.get(`${API}/admin/analytics/visitors`, authHeader());
    return response.data;
  },

  // Personal Settings
  getSettings: async () => {
    const response = await axios.get(`${API}/admin/settings`, authHeader());
    return response.data;
  },

  updateSettings: async (data) => {
    const response = await axios.put(`${API}/admin/settings`, data, authHeader());
    return response.data;
  }
};

export default api;